import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Anchor, Mail, Lock, User, Building, Phone, ArrowLeft, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function AgentRegister() {
  const navigate = useNavigate();
  const { register, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    phone: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name || null,
        phone: formData.phone || null,
      });
      logout(); // Log out since they need approval
      setRegistered(true);
      toast.success('Registration submitted! Please wait for admin approval.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Show pending approval message after registration
  if (registered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-8">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A] mb-4">
            Registration Submitted!
          </h1>
          <p className="text-slate-600 mb-6">
            Your account has been created and is pending approval. 
            An administrator will review your application shortly.
          </p>
          <p className="text-sm text-slate-500 mb-8">
            You will be able to log in once your account is approved.
          </p>
          <Link to="/">
            <Button className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1761143557505-e3ae3acfc1c3?w=1200)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 to-[#0F172A]/60" />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-sm flex items-center justify-center">
              <Anchor className="w-6 h-6 text-white" />
            </div>
            <span className="logo-text text-2xl text-white">
              Nile Cruise <span className="logo-accent">Plus</span>
            </span>
          </Link>
          
          <div>
            <h2 className="font-heading text-4xl text-white font-bold mb-4">
              Join Our Network
            </h2>
            <p className="text-white/70 max-w-md">
              Register as a travel agent to access premium Nile cruises, competitive rates, and a powerful booking platform.
            </p>
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <span className="text-[#D4AF37] text-sm">✓</span>
                </div>
                <span className="text-sm">Real-time inventory access</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <span className="text-[#D4AF37] text-sm">✓</span>
                </div>
                <span className="text-sm">Instant booking confirmation</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <span className="text-[#D4AF37] text-sm">✓</span>
                </div>
                <span className="text-sm">Dedicated support team</span>
              </div>
            </div>
          </div>
          
          <p className="text-white/40 text-sm">
            Already 500+ travel agencies trust Nile Cruise Plus
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8FAFC] overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-sm flex items-center justify-center">
              <Anchor className="w-6 h-6 text-white" />
            </div>
            <span className="logo-text text-2xl">
              Nile Cruise <span className="logo-accent">Plus</span>
            </span>
          </div>

          <h1 className="font-heading text-3xl font-bold text-[#0F172A] mb-2">
            Create Agent Account
          </h1>
          <p className="text-slate-500 mb-8">
            Fill in your details to get started
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  className="pl-10 h-12 input-nile"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="agent@company.com"
                  className="pl-10 h-12 input-nile"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-12 input-nile"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">Confirm *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-12 input-nile"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-slate-700">Company Name (Optional)</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="company"
                  type="text"
                  placeholder="Your Travel Agency"
                  className="pl-10 h-12 input-nile"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  data-testid="register-company-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-700">Phone Number (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+20 123 456 7890"
                  className="pl-10 h-12 input-nile"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 btn-primary rounded-sm text-base"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner w-5 h-5 border-2" />
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="text-center text-slate-500 mt-8">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-[#1D4ED8] font-medium hover:underline"
              data-testid="login-link"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
